import { Copy, Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { CurrencyInput } from "@/components/shared/currency-input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { formatFY } from "@/lib/fiscal";
import { cn } from "@/lib/utils";

// ── Types ────────────────────────────────────────────────────────────

export type GoalSectionType =
  | "metric-goals"
  | "non-relational-goals"
  | "affiliate-goals"
  | "source-goals"
  | "campus-targets"
  | "campus-affiliates"
  | "campus-budget"
  | "affiliate-mapping"
  | "created-goals"
  | "fp-reporting-goals";

export const GOAL_SECTION_CONFIG: Record<GoalSectionType, { label: string; description: string }> =
  {
    "metric-goals": {
      label: "Metric Goals",
      description: "Performance targets for key metrics",
    },
    "non-relational-goals": {
      label: "Non-Relational Goals",
      description: "Performance targets for non-relational deals",
    },
    "affiliate-goals": {
      label: "Affiliate Goals",
      description: "Performance targets for affiliate-sourced deals",
    },
    "source-goals": {
      label: "Source Goals",
      description: "Per-source performance metrics",
    },
    "campus-targets": {
      label: "Campus Targets",
      description: "Campus engagement metrics",
    },
    "campus-affiliates": {
      label: "Campus Affiliates",
      description: "Assigned campus partnerships",
    },
    "campus-budget": {
      label: "Campus Budget",
      description: "Annual campus budget allocation",
    },
    "affiliate-mapping": {
      label: "Affiliate Coaching",
      description: "Map affiliates to track bonus earnings",
    },
    "created-goals": {
      label: "Created Goals",
      description: "Shopify-sourced order targets (pop-up, web, gross sales)",
    },
    "fp-reporting-goals": {
      label: "FP Reporting Goals",
      description: "Lead generation and digital marketing metrics",
    },
  };

export const ALL_SECTION_TYPES: GoalSectionType[] = [
  "metric-goals",
  "non-relational-goals",
  "affiliate-goals",
  "source-goals",
  "campus-targets",
  "campus-affiliates",
  "campus-budget",
  "affiliate-mapping",
  "created-goals",
  "fp-reporting-goals",
];

// ── Metrics ──────────────────────────────────────────────────────────

export const AVAILABLE_METRICS = [
  { key: "avg_order_value", label: "Avg Order Value", type: "currency" as const },
  { key: "close_rate", label: "Close Rate", type: "percent" as const },
  { key: "closed_deals", label: "Closed Deals", type: "number" as const },
  { key: "error_rate", label: "Error Rate (Less than)", type: "percent" as const },
  { key: "gross_profit_margin", label: "Gross Profit Margin", type: "percent" as const },
  { key: "gross_sales", label: "Gross Sales", type: "currency" as const },
  { key: "opportunities", label: "Opportunities", type: "number" as const },
];

export const NR_METRIC_PREFIX = "nr_";

export const NR_METRICS = AVAILABLE_METRICS.map((m) => ({
  ...m,
  key: `${NR_METRIC_PREFIX}${m.key}`,
  label: `NR ${m.label}`,
}));

export const AFF_METRIC_PREFIX = "aff_";

export const AFF_METRICS = AVAILABLE_METRICS.map((m) => ({
  ...m,
  key: `${AFF_METRIC_PREFIX}${m.key}`,
  label: `Aff ${m.label}`,
}));

export const CREATED_METRIC_PREFIX = "created_";

export const CREATED_METRICS = [
  { key: `${CREATED_METRIC_PREFIX}popup_units`, label: "Pop Up Units", type: "number" as const },
  { key: `${CREATED_METRIC_PREFIX}web_units`, label: "Web Units", type: "number" as const },
  { key: `${CREATED_METRIC_PREFIX}total_units`, label: "Total Units", type: "number" as const },
  {
    key: `${CREATED_METRIC_PREFIX}gross_sales`,
    label: "Created Gross Sales",
    type: "currency" as const,
  },
  {
    key: `${CREATED_METRIC_PREFIX}popup_gross_sales`,
    label: "Pop Up Gross Sales",
    type: "currency" as const,
  },
];

export const FP_METRIC_PREFIX = "fp_";

export const FP_METRICS = [
  {
    key: `${FP_METRIC_PREFIX}lead_generation`,
    label: "FP Lead Generation",
    type: "number" as const,
  },
  {
    key: `${FP_METRIC_PREFIX}digital_lead_generation`,
    label: "FP Digital Total Leads",
    type: "number" as const,
  },
  {
    key: `${FP_METRIC_PREFIX}instagram_lead_generation`,
    label: "FP Instagram Lead Generation",
    type: "number" as const,
  },
  {
    key: `${FP_METRIC_PREFIX}tiktok_lead_generation`,
    label: "FP TikTok Lead Generation",
    type: "number" as const,
  },
  { key: `${FP_METRIC_PREFIX}digital_cac`, label: "FP Digital CAC", type: "currency" as const },
  {
    key: `${FP_METRIC_PREFIX}digital_close_rate`,
    label: "FP Digital Close Rate",
    type: "percent" as const,
  },
  {
    key: `${FP_METRIC_PREFIX}digital_gross_sales`,
    label: "FP Digital Gross Sales",
    type: "currency" as const,
  },
];

const ALL_METRICS = [
  ...AVAILABLE_METRICS,
  ...NR_METRICS,
  ...AFF_METRICS,
  ...CREATED_METRICS,
  ...FP_METRICS,
];

export function getMetricDef(key: string) {
  return ALL_METRICS.find((m) => m.key === key);
}

export const CAMPUS_METRICS = {
  leads: "campus_er_leads",
  dealsWon: "campus_er_deals_won",
} as const;

export interface GoalRow {
  metric: string;
  value: string;
}

// ── Save Flash Hook ──────────────────────────────────────────────────

export function useSaveFlash() {
  const [savedField, setSavedField] = useState<string | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  function flashSaved(field: string) {
    clearTimeout(savedTimerRef.current);
    setSavedField(field);
    savedTimerRef.current = setTimeout(() => setSavedField(null), 800);
  }

  return { savedField, flashSaved };
}

export const SAVED_BORDER = "border-green-500 ring-[3px] ring-green-500/20";

export function savedFieldClass(savedField: string | null, field: string) {
  return cn(
    "transition-[border-color,box-shadow] duration-300",
    savedField === field && SAVED_BORDER,
  );
}

// ── Shared Input Components ──────────────────────────────────────────

export function PercentInput({
  id,
  value,
  onChange,
  onBlur,
  placeholder,
  step = "0.1",
  autoFocus,
  className,
  disabled,
}: {
  id: string;
  value: string | number;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  step?: string;
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const displayValue = focused || value === "" ? String(value) : `${Number(value)}%`;

  return (
    <Input
      id={id}
      type={focused ? "number" : "text"}
      step={step}
      value={displayValue}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        onBlur?.();
      }}
      placeholder={placeholder}
      autoFocus={autoFocus}
      className={className}
      disabled={disabled}
    />
  );
}

export function GoalRowInput({
  goal,
  autoFocus = false,
  onChangeValue,
  onBlur,
  onRemove,
  savedField,
  disabled,
}: {
  goal: GoalRow;
  autoFocus?: boolean;
  onChangeValue: (value: string) => void;
  onBlur: () => void;
  onRemove?: () => void;
  savedField: string | null;
  disabled?: boolean;
}) {
  const def = getMetricDef(goal.metric);
  if (!def) return null;

  const inputClass = savedFieldClass(savedField, goal.metric);

  return (
    <div className="flex items-end gap-3">
      <div className="flex-1 space-y-1.5">
        <Label className="text-sm">{def.label}</Label>
        {def.type === "currency" ? (
          <CurrencyInput
            id={`target-${def.key}`}
            value={goal.value}
            onChange={onChangeValue}
            onBlur={onBlur}
            placeholder="Enter target"
            autoFocus={autoFocus}
            className={inputClass}
            disabled={disabled}
          />
        ) : def.type === "percent" ? (
          <PercentInput
            id={`target-${def.key}`}
            value={goal.value}
            onChange={onChangeValue}
            onBlur={onBlur}
            placeholder="Enter target"
            step="0.1"
            autoFocus={autoFocus}
            className={inputClass}
            disabled={disabled}
          />
        ) : (
          <Input
            id={`target-${def.key}`}
            type="number"
            value={goal.value}
            onChange={(e) => onChangeValue(e.target.value)}
            onBlur={onBlur}
            placeholder="Enter target"
            autoFocus={autoFocus}
            className={inputClass}
            disabled={disabled}
          />
        )}
      </div>
      {onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// ── Copy To FY Select ────────────────────────────────────────────────

export function CopyToFYSelect({
  currentFY,
  selectedFY,
  onCopy,
}: {
  currentFY: number;
  selectedFY: number;
  onCopy: (targetFY: number) => void;
}) {
  const copyFYOptions = Array.from({ length: 5 }, (_, i) => currentFY - 2 + i).filter(
    (fy) => fy !== selectedFY,
  );

  return (
    <Select onValueChange={(v) => onCopy(Number(v))} value="">
      <SelectTrigger className="w-[110px] h-8 text-xs">
        <div className="flex items-center gap-1.5">
          <Copy className="h-3 w-3" />
          Copy To
        </div>
      </SelectTrigger>
      <SelectContent>
        {copyFYOptions.map((fy) => (
          <SelectItem key={fy} value={String(fy)}>
            {formatFY(fy)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Section Card Header ──────────────────────────────────────────────

export function SectionHeader({
  title,
  onRemove,
  children,
}: {
  title: string;
  onRemove?: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-semibold">{title}</h3>
      <div className="flex items-center gap-2">
        {children}
        {onRemove && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove {title}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will delete all {title.toLowerCase()} data for this fiscal year. This action
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onRemove}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

// ── Add Goal Select (used within sections) ───────────────────────────

export function AddGoalSelect({
  availableMetrics: available,
  onAdd,
}: {
  availableMetrics: typeof AVAILABLE_METRICS;
  onAdd: (metricKey: string) => void;
}) {
  if (available.length === 0) return null;

  return (
    <div className="mt-4">
      <Select onValueChange={(v) => onAdd(v)} value="">
        <SelectTrigger className="w-[200px] h-9 text-sm">
          <div className="flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Add Goal
          </div>
        </SelectTrigger>
        <SelectContent>
          {available.map((m) => (
            <SelectItem key={m.key} value={m.key}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
